import './ReportFormSectionConfig.scss';
import { HvacArea, HvacGroup } from '@belimo-retrofit-portal/logic';
import { FieldPath } from '@form-ts/core';
import { ReadonlyRecord } from 'fp-ts/ReadonlyRecord';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button } from 'src/modules/common/components/Button';
import { HorizontalDivider } from 'src/modules/common/components/HorizontalDivider';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { Project } from 'src/modules/common/types/Project';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { BodySmall01 } from 'src/modules/common/typography/BodySmall01';
import { CheckboxField } from 'src/modules/form/components/CheckboxField';
import { FormError } from 'src/modules/form/types/FormError';
import { HVAC_GROUP_AREAS } from 'src/modules/hvac/constants/group';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGeneratedHvacGroup } from 'src/modules/report/types/ReportGenerated';
import { ReportFormFieldToggle } from 'src/modules/report/views/ReportFormFieldToggle';

type Props = {
  readonly group: ReportGeneratedHvacGroup;
  readonly field: FieldPath<ReportFormData, FormError, ReportFormData['hvac'][number]>;
  readonly project: Project;
};

export const ReportFormSectionConfigHvac = ({ group, field, project }: Props): React.ReactElement => (
  <div className="bp-report-form-section-config">
    <div className="bp-report-form-section-config__header">
      <div className="bp-report-form-section-config__header-title">
        <BodySmall01>
          <FormattedMessage id="report/form/hvac/header"/>
        </BodySmall01>

        <BodyMedium01>
          {group.title || <>{GROUP_TITLES[group.group]}&nbsp;{group.number}</>}
        </BodyMedium01>
      </div>

      <CheckboxField field={field.at('enabled')}/>
    </div>

    <HorizontalDivider/>

    <div className="bp-report-form-section-config__content">
      {HVAC_GROUP_AREAS[group.group].map((areaKey) => (
        <ReportFormFieldToggle
          key={areaKey}
          field={field.at('showAreas').at(areaKey)}
          disabled={!project.data.hvac.areas[areaKey].enabled || group.empty}
        >
          {AREA_TITLES[areaKey]}
        </ReportFormFieldToggle>
      ))}

      <HorizontalDivider/>

      <ReportFormFieldToggle field={field.at('showSchemas')} disabled={group.empty}>
        <FormattedMessage id="report/form/hvac/showSchemas"/>
      </ReportFormFieldToggle>

      <ReportFormFieldToggle field={field.at('showComments')} disabled={group.empty}>
        <FormattedMessage id="report/form/hvac/showComments"/>
      </ReportFormFieldToggle>

      <ReportFormFieldToggle field={field.at('showDetails')} disabled={group.empty}>
        <FormattedMessage id="report/form/hvac/showDetails"/>
      </ReportFormFieldToggle>
    </div>

    <div className="bp-report-form-section-config__action">
      <Button
        fullWidth={true}
        as={Link}
        to={`/project/${encodeURIComponent(project.id)}/hvac`}
        kind="tertiary"
        size="md"
      >
        <FormattedMessage id="report/form/hvac/editLinkText"/>

        <div className="bp-report-form-section-config__button-icon">
          <ArrowRightIcon/>
        </div>
      </Button>
    </div>
  </div>
);

const AREA_TITLES = {
  [HvacArea.HEATING]: <FormattedMessage id="report/form/hvac/showHeating"/>,
  [HvacArea.COOLING]: <FormattedMessage id="report/form/hvac/showCooling"/>,
  [HvacArea.VENTILATION]: <FormattedMessage id="report/form/hvac/showVentilation"/>,
};

const GROUP_TITLES = {
  [HvacGroup.EMISSION]: <FormattedMessage id="report/document/hvac/group/emission"/>,
  [HvacGroup.DISTRIBUTION]: <FormattedMessage id="report/document/hvac/group/distribution"/>,
  [HvacGroup.AIR_HANDLER]: <FormattedMessage id="report/document/hvac/group/air_handler"/>,
  [HvacGroup.GENERATION]: <FormattedMessage id="report/document/hvac/group/generation"/>,
};

const HVAC_GROUP_AREAS_ROUTES: ReadonlyRecord<HvacGroup, string> = {
  [HvacGroup.EMISSION]: 'emission',
  [HvacGroup.DISTRIBUTION]: 'distribution',
  [HvacGroup.AIR_HANDLER]: 'distribution',
  [HvacGroup.GENERATION]: 'generation',
};
